import React, { Component } from 'react';
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/async';
import { Modal } from 'react-bootstrap';
import { putRequest, postRequest, getRequest } from '../../../requestUtils';
import { analyticsTrack } from '../../../analyticsUtils';
import infoIcon from '../../../../assets/images/icons/blue-info-icon.svg';
import ExpertiseTagDropdown from '../../common/ExpertiseTagDropdown';
import '../ProjectDetails.scss';
import FilterTag from '../../common/FilterTag';
import customStyles from '../../../dropdownStyleUtils';
import DropdownIndicator from '../../common/DropdownIndicator';

const formStep = 'start';

const filterPicklist = (picklist, input) => picklist.filter(
  (record) => record.label.toLowerCase().includes(input.toLowerCase()),
);

const handleTrackEvent = () => analyticsTrack('Navigated away from new project', 'Project creation');

const numericKeyFilter = (e) => {
  const re = /[0-9]/;
  if (e.keyCode === 8 || e.keyCode === 46 || e.keyCode === 37 || e.keyCode === 38
    || e.keyCode === 39 || e.keyCode === 40) {
    return;
  }
  if (!re.test(e.key)) {
    e.preventDefault();
  }
};

class ProjectName extends Component {
  constructor(props) {
    super(props);

    this.state = {
      projectName: props.projectName,
      showModal: false,
      errors: null,
      selectedProjectLength: null,
      companyId: props.companyId,
      expertiseTags: props.newProjectDetails.expertise_tags,
      expertisePicklists: props.newProjectDetails.expertise_picklists,
      projectTemplates: [],
      selectedAvgHoursPerWeek: {},
      hoursPerWeekPicklist: props.hoursPerWeekPicklist || [],
      employmentType: props.newProjectDetails.employment_type,
    };

    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleHideModal = this.handleHideModal.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleRoleSelect = this.handleRoleSelect.bind(this);
    this.handleLevelSelect = this.handleLevelSelect.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.handleEmploymentTypeSelect = this.handleEmploymentTypeSelect.bind(this);
    this.handleSelectTemplate = this.handleSelectTemplate.bind(this);
    this.setName = this.setName.bind(this);
    this.getProjectTemplates = this.getProjectTemplates.bind(this);
  }

  componentDidMount() {
    const {
      projectResourceId, getProjectDetails, projectTemplatesPath, projectTemplateId,
    } = this.props;

    if (projectResourceId) {
      getProjectDetails(formStep).then((response) => {
        const { data } = response;
        this.setState({
          projectName: data.name,
          companyId: data.company_id,
          expertiseTags: data.expertise_tags,
          expertisePicklists: data.expertise_picklists,
          selectedProjectLength: data.duration_range,
          selectedAvgHoursPerWeek: {
            value: data.avg_time_required_range,
            label: data.avg_time_required_range ? `${data.avg_time_required_range} hours` : null,
          },
          hoursPerWeekPicklist: data.hours_picklist,
        });
      });
    } else {
      window.addEventListener('beforeunload', handleTrackEvent);
    }

    if (projectTemplatesPath) {
      this.getProjectTemplates(() => this.handleSelectTemplate(projectTemplateId));
    }
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', handleTrackEvent);
  }

  getProjectTemplates(callback) {
    const { projectTemplatesPath } = this.props;

    getRequest(projectTemplatesPath).then((response) => {
      this.setState({
        projectTemplates: response.data,
      }, callback);
    });
  }

  setName(e) {
    this.setState({ projectName: e.target.value });
  }

  canSubmit() {
    const { isAdmin } = this.props;
    const {
      projectName, selectedProjectLength, expertiseTags,
      selectedAvgHoursPerWeek, companyId, employmentType,
    } = this.state;

    return (
      projectName.length > 3 && (!isAdmin || companyId)
        && expertiseTags.higher_level_role_ids.length && expertiseTags.level_ids.length
        && (employmentType === 'full_time' || (employmentType === 'consulting'
          && selectedProjectLength && selectedProjectLength > 0 && selectedProjectLength <= 24
          && selectedAvgHoursPerWeek && selectedAvgHoursPerWeek.value)
        )
    );
  }

  handleSelectTemplate(templateId) {
    const { projectTemplates, expertiseTags } = this.state;
    const { userId } = this.props;
    if (!templateId) return;
    const chosenTemplate = projectTemplates.find((template) => (
      template.id === templateId
    ));
    const allTags = expertiseTags;
    allTags.level_ids = chosenTemplate.seniorities;
    allTags.higher_level_role_ids = chosenTemplate.roles;

    analyticsTrack(
      `Selected Project Template: ${chosenTemplate.name}`,
      {
        templateName: chosenTemplate.name,
        eventId: 'templateButtonClickEvent',
        eventType: 'buttonClick',
        timestamp: Date.now(),
        userId,
      },
    );

    this.setState({
      selectedTemplate: templateId,
      expertiseTags: allTags,
      projectName: chosenTemplate.name,
    });
  }

  handleRoleSelect() {
    const { expertiseTags } = this.state;

    return ((tags) => {
      const allTags = expertiseTags;
      allTags.higher_level_role_ids = tags.length >= 1 ? [tags[tags.length - 1].value] : [];
      if (tags.length >= 1) {
        analyticsTrack(
          `Selected Higher Level Role: ${tags[tags.length - 1].label}`,
          { location: 'Project creation' },
        );
      }
      this.setState({ expertiseTags: allTags });
    });
  }

  handleEmploymentTypeSelect(choice) {
    this.setState({ employmentType: choice });
  }

  handleLevelSelect() {
    const { expertiseTags } = this.state;

    return ((tags) => {
      const allTags = expertiseTags;
      allTags.level_ids = tags.map((t) => t.value);
      if (tags.length >= 1) {
        analyticsTrack(
          `Selected Seniority: ${tags[tags.length - 1].label}`,
          { location: 'Project creation' },
        );
      }
      this.setState({ expertiseTags: allTags });
    });
  }

  handleShowModal() {
    this.setState({ showModal: true });
  }

  handleHideModal() {
    this.setState({ showModal: false });
  }

  handleBack() {
    if (this.props.formType === 'page') {
      this.props.back();
    } else {
      this.handleHideModal();
    }
  }

  handleSelect(opt) {
    this.setState({ companyId: opt.value });
  }

  handleSubmit() {
    const {
      csrfToken, projectUrl, projectResourceId, afterSubmit,
      setProjectResourceId, isAdmin, favoritedConsultant,
    } = this.props;
    const {
      projectName, companyId, expertiseTags, selectedProjectLength,
      selectedAvgHoursPerWeek, selectedTemplate, employmentType,
    } = this.state;

    const data = {
      name: projectName,
      higher_level_role_ids: expertiseTags.higher_level_role_ids,
      level_ids: expertiseTags.level_ids,
      duration_range: selectedProjectLength,
      avg_time_required_range: selectedAvgHoursPerWeek.value,
      employment_type: employmentType,
    };
    if (favoritedConsultant) {
      data.favorited_consultant = favoritedConsultant;
    }
    if (isAdmin) {
      data.company_id = companyId;
    }
    if (this.props.rehireUserResourceId) {
      data.rehire_user_resource_id = this.props.rehireUserResourceId;
    }

    if (projectResourceId) {
      putRequest(
        `/projects/${projectResourceId}`,
        { project: data },
        csrfToken,
      ).then(() => {
        afterSubmit();
      }).catch((error) => {
        this.setState({ errors: error.name });
      });
    } else {
      if (this.props.duplicationProjectResourceId) {
        data.project_resource_id = this.props.duplicationProjectResourceId;
      }
      postRequest(
        projectUrl,
        {
          project: data,
          template: selectedTemplate,
        },
        csrfToken,
      ).then((response) => {
        setProjectResourceId(afterSubmit)(response.data.resource_id);
      }).catch((error) => {
        this.setState({ errors: error.name });
      });
    }
  }

  renderFlashMessages() {
    const { jobDescriptionUpload, projectTemplateId } = this.props;

    if (jobDescriptionUpload || projectTemplateId) {
      return (
        <div className="info-blurb mb-2">
          <img src={infoIcon} alt="info-icon" />
          <div className="message">
            <h3>
              { jobDescriptionUpload && "We've imported your job description" }
              { projectTemplateId && "We've pre-filled the project info from the template you selected" }
              <span className="ms-2" role="img" aria-label="thumbs-up">👍</span>
            </h3>
            <span>Check the details and edit as needed.</span>
          </div>
        </div>
      );
    }
    return null;
  }

  renderModal() {
    return (
      <Modal
        show={this.state.showModal}
        onHide={this.handleHideModal}
        dialogClassName="project-name-modal"
      >
        <Modal.Header closeButton />
        <Modal.Body className="project-name-modal-body">
          {this.renderContent()}
        </Modal.Body>
      </Modal>
    );
  }

  renderButtons() {
    const { backButtonText, submitButtonText } = this.props;

    return (
      <div className="footer-buttons-container mt-4">
        <button
          className="btn btn-secondary button-back"
          onClick={this.handleBack}
        >
          {backButtonText}
        </button>
        <button
          className="btn btn-primary button-submit text-white"
          onClick={this.handleSubmit}
          disabled={!this.canSubmit()}
        >
          {submitButtonText}
        </button>
      </div>
    );
  }

  renderProjectLengthQuestion() {
    const { selectedProjectLength } = this.state;

    return (
      <div className="project-length-question mt-3">
        <h3>
          Length of engagement
          <span className="heading-required">* Required</span>
        </h3>
        <div className="project-length-container mt-2">
          <input
            placeholder=""
            type="number"
            id="project-length"
            name="project-length"
            value={selectedProjectLength}
            onKeyDown={numericKeyFilter}
            onChange={(e) => this.setState({ selectedProjectLength: e.target.value })}
          />
          <span className="ms-2">months</span>
        </div>
        { selectedProjectLength && selectedProjectLength > 24
          && (
          <div className="error-text">
            Cannot be more than 24 months.
          </div>
          )}
      </div>
    );
  }

  renderProjectHoursQuestion() {
    const { selectedAvgHoursPerWeek, hoursPerWeekPicklist } = this.state;

    return (
      <div className="project-hours-question mt-3">
        <h3>
          Average hours per week
          <span className="heading-required">* Required</span>
        </h3>
        <div className="project-hours-container mt-2">
          <AsyncSelect
            components={{ DropdownIndicator, IndicatorSeparator: () => null }}
            classNamePrefix="clora-multiselect"
            styles={customStyles}
            name="project-hours"
            id="project-hours"
            className="project-hours-select"
            defaultOptions={hoursPerWeekPicklist.map((item) => ({ value: item, label: `${item} hours` }))}
            placeholder="Select hours"
            value={selectedAvgHoursPerWeek}
            onChange={(selected) => this.setState({ selectedAvgHoursPerWeek: selected })}
          />
        </div>
      </div>
    );
  }

  renderErrors() {
    const { errors } = this.state;

    return (errors
      && (
      <div id="error_explanation">
        <h3>Errors prohibited this project from being saved:</h3>
        <ul>
          {errors.map((e) => (
            <li>{e}</li>
          ))}
        </ul>
      </div>
      )
    );
  }

  renderContent() {
    const {
      isAdmin, companyPicklist, newProjectDetails, showFullTimeOption,
    } = this.props;
    const {
      companyId, expertiseTags, expertisePicklists, employmentType,
    } = this.state;
    const selection = (isAdmin && companyId)
      ? companyPicklist.find((opt) => opt.value === companyId) : null;
    const roleSelection = expertiseTags.higher_level_role_ids.map((id) => (
      expertisePicklists.higher_level_role.find((o) => (o.value === id))
    ));
    const levelSelection = expertiseTags.level_ids.map((id) => (
      expertisePicklists.level.find((o) => (o.value === id))
    ));

    return (
      <div className="style-guide project-name-container">
        {isAdmin
          && (
          <div className="g-row">
            <div className="g-column g-flex-1" />
            <div className="g-column g-flex-6" />
          </div>
          )}
        <div className="g-row">
          <div className="g-column g-flex-1" />
          <div className="g-column g-flex-6 name-entry-container">
            {this.renderErrors()}
            {this.renderFlashMessages()}
            {isAdmin
              && (
              <div>
                <h2 className="v3">What company is this project for?</h2>
                <br />
                <AsyncSelect
                  components={{ DropdownIndicator, IndicatorSeparator: () => null }}
                  styles={customStyles}
                  classNamePrefix="clora-multiselect"
                  closeMenuOnSelect
                  value={selection}
                  className="project-company-select"
                  id="project-company-select"
                  onChange={this.handleSelect}
                  placeholder="Search"
                  marginTop="5"
                  defaultOptions={companyPicklist}
                  loadOptions={(input) => (
                    new Promise((resolve) => { resolve(filterPicklist(companyPicklist, input)); })
                  )}
                  cacheOptions
                  isSearchable
                />
                <br />
              </div>
              )}
            <h2 className="v3">
              High-Level Information about this Project
            </h2>
            <h3 className="mt-3">
              Title
              <span className="heading-required">* Required</span>
            </h3>
            <div className="input-sub-heading">
              Use a descriptive title since this is one of
              the first thing candidates will see.
              {' '}
              <br />
              Example: Medical Writer to Support IND Submission for Oncology Program
            </div>
            <input
              name="projectName"
              id="projectName"
              className="project-name-entry"
              required="required"
              value={this.state.projectName}
              onChange={this.setName}
              placeholder="Project title"
            />
            <div className="project-expertise-select higher_level_role mt-2">
              <ExpertiseTagDropdown
                model="higher_level_role"
                selection={roleSelection}
                handleSelect={this.handleRoleSelect}
                multi={false}
                isValidNewOption={() => false}
                isCreatable={false}
                heading="Role"
                isDisabled={false}
                picklist={newProjectDetails.expertise_picklists.higher_level_role}
                loadOptions={(input) => (
                  new Promise((resolve) => {
                    resolve(filterPicklist(newProjectDetails.expertise_picklists.higher_level_role, input));
                  }))}
              />
            </div>
            <div className="project-expertise-select level mt-2">
              <ExpertiseTagDropdown
                model="level"
                selection={levelSelection}
                handleSelect={this.handleLevelSelect}
                handleCreate={() => {}}
                multi
                isValidNewOption={() => false}
                isCreatable={false}
                heading="Seniority"
                menuHeading="What level of seniority will your project need?"
                isDisabled={false}
                picklist={newProjectDetails.expertise_picklists.level}
                checkboxOptions
                loadOptions={(input) => (
                  new Promise((resolve) => {
                    resolve(filterPicklist(expertisePicklists.level, input));
                  }))}
              />
            </div>
            { showFullTimeOption && this.renderEmploymentChoice() }
            { (!showFullTimeOption || employmentType === 'consulting') && [
              this.renderProjectLengthQuestion(),
              this.renderProjectHoursQuestion(),
            ] }
            {this.renderButtons()}
          </div>
        </div>
      </div>
    );
  }

  renderModalLink() {
    return (
      <i
        role="button"
        tabIndex={0}
        className="fa fa-edit modal-link"
        onClick={this.handleShowModal}
      >
        {(this.props.formType === 'modal-link') ? 'Edit' : ''}
      </i>
    );
  }

  renderEmploymentChoice() {
    const { newProjectDetails } = this.props;
    const { employmentType } = this.state;

    return (
      <div className="employment-type-container">
        <h3>
          Employment Type
          <span className="heading-required">* Required</span>
        </h3>
        <FilterTag
          options={newProjectDetails.employment_type_options}
          handleChange={this.handleEmploymentTypeSelect}
          selected={employmentType}
        />
      </div>
    );
  }

  render() {
    if (this.props.formType === 'page') {
      return this.renderContent();
    }
    return (
      <div>
        { this.renderModalLink() }
        { this.renderModal() }
      </div>
    );
  }
}

ProjectName.propTypes = {
  formType: PropTypes.string, // one of 'page', 'modal-link', 'modal-icon'
  projectResourceId: PropTypes.string,
  projectUrl: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  projectName: PropTypes.string,
  afterSubmit: PropTypes.func,
  back: PropTypes.func,
  setProjectResourceId: PropTypes.func.isRequired,
  getProjectDetails: PropTypes.func.isRequired,
  companyId: PropTypes.string,
  isAdmin: PropTypes.bool,
  companyPicklist: PropTypes.arrayOf(PropTypes.shape({})),
  duplicationProjectResourceId: PropTypes.string,
  rehireUserResourceId: PropTypes.string,
  backButtonText: PropTypes.string,
  submitButtonText: PropTypes.string,
  favoritedConsultant: PropTypes.string,
  newProjectDetails: PropTypes.shape({
    expertise_picklists: PropTypes.shape({}),
    expertise_tags: PropTypes.shape({}),
    employment_type: PropTypes.string,
  }),
  userId: PropTypes.string.isRequired,
  projectTemplatesPath: PropTypes.string,
  hoursPerWeekPicklist: PropTypes.arrayOf(PropTypes.shape({})),
  showFullTimeOption: PropTypes.bool.isRequired,
  projectTemplateId: PropTypes.string,
  jobDescriptionUpload: PropTypes.bool,
};

ProjectName.defaultProps = {
  formType: 'page',
  projectResourceId: null,
  duplicationProjectResourceId: null,
  rehireUserResourceId: null,
  projectName: '',
  afterSubmit: () => {},
  back: () => {},
  companyId: '',
  isAdmin: false,
  companyPicklist: [],
  backButtonText: 'Back',
  submitButtonText: 'Next',
  favoritedConsultant: null,
  newProjectDetails: {},
  projectTemplatesPath: null,
  hoursPerWeekPicklist: [],
  projectTemplateId: null,
  jobDescriptionUpload: false,
};

export default ProjectName;
